import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Tags, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { FileMakerSyncCard } from "@/components/FileMakerSyncCard";
import { ProcurementSyncCard } from "@/components/ProcurementSyncCard";
import { ProductOverviewSyncCard } from "@/components/ProductOverviewSyncCard";
import { ContractOrderSyncCard } from "@/components/ContractOrderSyncCard";
import { UpcSyncCard } from "@/components/UpcSyncCard";
import { ExternalApiPanel } from "@/components/ExternalApiPanel";
import { ApiTokensPanel } from "@/components/ApiTokensPanel";
import { ProductTypesCard } from "@/components/ProductTypesCard";

const OPTION_LISTS = [
  ["buyers", "products.buyer"],
  ["sellers", "products.seller"],
  ["categories", "products.category"],
  ["brands", "products.brand"],
];

const OptionListEditor = ({ listKey, label, values, onChange, placeholder }) => {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput("");
  };
  return (
    <div className="space-y-2" data-testid={`option-list-${listKey}`}>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-1.5 min-h-[30px]">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-secondary/50 text-xs font-medium" data-testid={`option-chip-${listKey}-${v}`}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} data-testid={`option-remove-${listKey}-${v}`} className="text-muted-foreground hover:text-destructive transition-colors">
              <X size={11} />
            </button>
          </span>
        ))}
        {values.length === 0 && <span className="text-xs text-muted-foreground">-</span>}
      </div>
      <div className="flex gap-1.5">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`option-input-${listKey}`}
          className="h-8 text-xs"
        />
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`option-add-${listKey}`} className="rounded-md h-8">
          +
        </Button>
      </div>
    </div>
  );
};

// Subcategories are edited as list of {name, category} — each row is a name input
// plus a parent-category dropdown, per user spec 2026-02 ("subcategory 需要注明其所属的 category").
const SubcategoryEditor = ({ subcategories, categories, onChange, t, placeholder }) => {
  const [name, setName] = useState("");
  const [cat, setCat] = useState("");
  const add = () => {
    const n = name.trim();
    if (!n) return;
    // Reject exact duplicate (same name + same parent category)
    if (subcategories.some((sc) => sc.name === n && sc.category === cat)) return;
    onChange([...subcategories, { name: n, category: cat }]);
    setName("");
  };
  // Group for display: {"category": [row, row, ...]}
  const grouped = subcategories.reduce((acc, sc) => {
    const key = sc.category || "__unassigned__";
    (acc[key] = acc[key] || []).push(sc);
    return acc;
  }, {});
  const orderedCats = [
    ...categories.filter((c) => grouped[c]),
    ...(grouped["__unassigned__"] ? ["__unassigned__"] : []),
  ];
  return (
    <div className="space-y-3" data-testid="option-list-subcategories">
      <Label>{t("products.subcategory")}</Label>
      {orderedCats.length === 0 && <span className="text-xs text-muted-foreground">-</span>}
      {orderedCats.map((c) => (
        <div key={c} className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`subcat-group-${c}`}>
            {c === "__unassigned__" ? t("settings.subcategoryUnassigned") : c}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {grouped[c].map((sc, idx) => (
              <span key={`${sc.name}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-secondary/50 text-xs font-medium" data-testid={`subcat-chip-${sc.category || "none"}-${sc.name}`}>
                {sc.name}
                <button type="button" onClick={() => onChange(subcategories.filter((x) => !(x.name === sc.name && x.category === sc.category)))} data-testid={`subcat-remove-${sc.category || "none"}-${sc.name}`} className="text-muted-foreground hover:text-destructive transition-colors">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        </div>
      ))}
      <div className="flex gap-1.5">
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="h-8 rounded-md border border-input bg-background text-xs px-2 min-w-[100px]"
          data-testid="subcat-parent-select"
        >
          <option value="">{t("settings.subcategoryPickCategory")}</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid="subcat-name-input"
          className="h-8 text-xs"
        />
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="subcat-add" className="rounded-md h-8">
          +
        </Button>
      </div>
    </div>
  );
};

export default function Settings() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [form, setForm] = useState({
    quote_exchange_rate: 0,
    usd_to_cad_rate: 1.4,
    shipping_fee: 0,
    cad_profit_rate: 0,
    usd_profit_rate: 0,
    new_product_days: 14,
    buyers: [], sellers: [], categories: [], subcategories: [], brands: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      setForm(data);
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in-up max-w-2xl">
      <h1 className="font-heading text-base font-bold tracking-tight mb-2 flex items-center gap-2">
        <SettingsIcon size={20} /> {t("settings.title")}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">{t("settings.subtitle")}</p>

      <div className="bg-card border border-border rounded-md p-6 space-y-5 mb-5">
        <div className="grid grid-cols-2 items-center gap-4">
          <div>
            <Label>{t("settings.quoteExchangeRate")}</Label>
            <p className="text-[11px] text-muted-foreground mt-1">{t("settings.exchangeRateHint")}</p>
          </div>
          <Input
            type="number"
            step="0.01"
            value={form.quote_exchange_rate ?? 0}
            onChange={(e) => setForm((f) => ({ ...f, quote_exchange_rate: parseFloat(e.target.value) || 0 }))}
            data-testid="settings-quote_exchange_rate-input"
          />
        </div>
        <div className="grid grid-cols-2 items-center gap-4">
          <div>
            <Label>{t("settings.usdToCadRate")}</Label>
            <p className="text-[11px] text-muted-foreground mt-1">{t("settings.usdToCadRateHint")}</p>
          </div>
          <Input
            type="number"
            step="0.01"
            value={form.usd_to_cad_rate ?? 1.4}
            onChange={(e) => setForm((f) => ({ ...f, usd_to_cad_rate: parseFloat(e.target.value) || 0 }))}
            data-testid="settings-usd_to_cad_rate-input"
          />
        </div>
        <div className="grid grid-cols-2 items-center gap-4">
          <div>
            <Label>{t("settings.shippingFee")}</Label>
            <p className="text-[11px] text-muted-foreground mt-1">{t("settings.shippingFeeHint")}</p>
          </div>
          <Input
            type="number"
            step="1"
            min="0"
            value={form.shipping_fee ?? 0}
            onChange={(e) => setForm((f) => ({ ...f, shipping_fee: parseFloat(e.target.value) || 0 }))}
            data-testid="settings-shipping_fee-input"
          />
        </div>
        <div className="grid grid-cols-2 items-center gap-4">
          <div>
            <Label>{t("settings.usdProfitRate")}</Label>
            <p className="text-[11px] text-muted-foreground mt-1">{t("settings.profitRateHint")}</p>
          </div>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="0.99"
            value={form.usd_profit_rate ?? 0}
            onChange={(e) => setForm((f) => ({ ...f, usd_profit_rate: parseFloat(e.target.value) || 0 }))}
            data-testid="settings-usd_profit_rate-input"
          />
        </div>
        <div className="grid grid-cols-2 items-center gap-4">
          <div>
            <Label>{t("products.newProductDays")}</Label>
            <p className="text-[11px] text-muted-foreground mt-1">{t("products.newProductDaysHint")}</p>
          </div>
          <Input
            type="number"
            step="1"
            min="1"
            max="365"
            value={form.new_product_days ?? 14}
            onChange={(e) => setForm((f) => ({ ...f, new_product_days: parseInt(e.target.value, 10) || 0 }))}
            data-testid="settings-new_product_days-input"
          />
        </div>
        <div className="grid grid-cols-2 items-center gap-4">
          <div>
            <Label>{t("settings.cadProfitRate")}</Label>
            <p className="text-[11px] text-muted-foreground mt-1">{t("settings.profitRateHint")}</p>
          </div>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="0.99"
            value={form.cad_profit_rate ?? 0}
            onChange={(e) => setForm((f) => ({ ...f, cad_profit_rate: parseFloat(e.target.value) || 0 }))}
            data-testid="settings-cad_profit_rate-input"
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-5 mb-5" data-testid="settings-options">
        <h3 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
          <Tags size={16} className="text-brand" /> {t("settings.options")}
        </h3>
        <p className="text-xs text-muted-foreground -mt-3">{t("settings.optionsHint")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {OPTION_LISTS.map(([key, labelKey]) => (
            <OptionListEditor
              key={key}
              listKey={key}
              label={t(labelKey)}
              values={form[key] || []}
              onChange={(vals) => setForm((f) => ({ ...f, [key]: vals }))}
              placeholder={t("settings.addOption")}
            />
          ))}
          <div className="md:col-span-2">
            <SubcategoryEditor
              subcategories={form.subcategories || []}
              categories={form.categories || []}
              onChange={(vals) => setForm((f) => ({ ...f, subcategories: vals }))}
              placeholder={t("settings.addOption")}
              t={t}
            />
          </div>
        </div>
      </div>

      {["admin", "export", "gt_export"].includes(user?.role) && <ProductTypesCard />}
      {user?.role === "admin" && <FileMakerSyncCard />}
      {user?.role === "admin" && <ProcurementSyncCard />}
      {user?.role === "admin" && <ProductOverviewSyncCard />}
      {user?.role === "admin" && <ContractOrderSyncCard />}
      {user?.role === "admin" && <UpcSyncCard />}
      {user?.role === "admin" && <ExternalApiPanel />}
      {/* 令牌在这里创建 / 作废（外部 App 用 readonly） */}
      {user?.role === "admin" && <ApiTokensPanel />}

      <Button onClick={handleSave} disabled={saving} data-testid="save-settings-button" className="rounded-md bg-brand hover:bg-brand-hover transition-colors px-8">
        {saving ? "..." : t("common.save")}
      </Button>
    </div>
  );
}
